// The lore pages in the world, drawn (phase E1): a small folded page on a stake at each page's spot, in the low-poly
// look on its one surface material (no new program after ready), the page glowing a little and turning slowly until it
// is found, then gone. A view: it reads the meta layer and never writes it.
import * as THREE from "three";
import { facet, merge, seeded } from "@rpg/meshkit";
import type { LowPolyLook } from "@rpg/look";
import type { Runtime } from "@rpg/runtime";
import type { MetaLayer } from "./module";

/** The page's geometry: a stake and a folded sheet over it. */
function pageGeometry(): THREE.BufferGeometry {
  const stake = facet(new THREE.CylinderGeometry(0.03, 0.04, 0.9, 5).translate(0, 0.45, 0), { colour: new THREE.Color("#6b4f33"), jitter: 0, tint: 0.06, rng: seeded(3) });
  // two leaves of a thin board, folded open towards the viewer (boxes: the one surface material draws front faces only)
  const leaf = (yaw: number, x: number, seed: number) => facet(new THREE.BoxGeometry(0.32, 0.42, 0.012).rotateY(yaw).translate(x, 1.05, 0), { colour: new THREE.Color("#f3e6c4"), jitter: 0, tint: 0.05, rng: seeded(seed), glow: 0.6 });
  return merge([stake, leaf(0.35, -0.08, 5), leaf(-0.35, 0.08, 7)]);
}

/** Draw the unfound pages; call `frame` once per drawn frame. */
export function mountLorePages(o: { runtime: Runtime; scene: THREE.Scene; look: LowPolyLook; layer: MetaLayer; ground: (x: number, z: number) => number }) {
  const root = new THREE.Group();
  root.name = "meta:lore-pages";
  const geo = pageGeometry();
  const meshes = new Map<string, THREE.Mesh>();
  for (const p of o.layer.pages()) {
    const m = new THREE.Mesh(geo, o.look.surface);
    m.position.set(p.x, o.ground(p.x, p.z), p.z);
    m.name = `page:${p.id}`;
    m.castShadow = false;
    root.add(m);
    meshes.set(p.id, m);
  }
  o.scene.add(root);
  o.look.track(root);
  o.runtime.on(
    "late",
    () => {
      const t = o.runtime.clock.time;
      for (const p of o.layer.pages()) {
        const m = meshes.get(p.id)!;
        m.visible = !p.found;
        m.rotation.y = t * 0.6;
      }
    },
    { order: 20, name: "meta: lore pages" },
  );
  return { shown: (): string[] => [...meshes].filter(([, m]) => m.visible).map(([id]) => id) };
}
